import { FC, HTMLAttributes, ReactNode, useMemo, useRef } from 'react';
import { Badge, UncontrolledTooltip } from 'reactstrap';

import { times } from 'lodash-es';
import { v4 as uuidv4 } from 'uuid';

import classNames from '../../helpers/classNames';
import { formatTime } from '../../helpers/datetime';
import { formatCurrency } from '../../helpers/formatCurrency';
import useTranslate from '../../hooks/useTranslate';
import { TeeTimeType } from '../../services/apis/teeTimes/types';
import useStore from '../../store/useStore';
import Tooltip from '../Tooltip/Tooltip';
import styles from './TeeTime.module.scss';

const MAX_PLAYERS = 4;

interface TeeTimeProps extends HTMLAttributes<HTMLButtonElement> {
  teeTime: TeeTimeType;
  currency: string;
  selectedNumberOfPlayers?: number;
  highlighted?: boolean;
  tooltip?: ReactNode;
}

const TeeTime: FC<TeeTimeProps> = props => {
  const {
    teeTime,
    currency,
    selectedNumberOfPlayers = 1,
    highlighted,
    className,
    tooltip,
    ...rest
  } = props;
  const { translate, activeLanguage } = useTranslate();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const isGolfhaftetRequest = useStore(state => state.isGolfhaftetRequest);

  const isDisabled = selectedNumberOfPlayers > teeTime.availablePlayers;

  const teeTimeId = useMemo(() => `tee-time-${uuidv4()}`, []);

  return (
    <>
      <button
        ref={buttonRef}
        id={teeTimeId}
        disabled={isDisabled}
        className={classNames(
          className,
          'd-flex flex-column align-items-center gap-2 p-2 rounded-2 lh-sm text-center text-body',
          styles.item,
          isDisabled && styles.disabled,
          highlighted && styles.highlighted
        )}
        {...rest}
      >
        <time
          dateTime={teeTime.dateTime}
          className="border-bottom border-success fw-bold pb-1 px-2"
        >
          {formatTime(teeTime.dateTime, activeLanguage.code)}
        </time>

        {teeTime.price !== 0 && (
          <div className="d-flex flex-column gap-1 align-items-center">
            {isGolfhaftetRequest && !!teeTime.rackPrice && teeTime.rackPrice > teeTime.price && (
              <Badge color="danger" pill>
                {Number(teeTime.price / teeTime.rackPrice - 1).toLocaleString(undefined, {
                  style: 'percent',
                })}
              </Badge>
            )}

            <div className="d-flex gap-1">
              <strong
                className={
                  isGolfhaftetRequest && !!teeTime.rackPrice && teeTime.rackPrice > teeTime.price
                    ? 'text-danger'
                    : undefined
                }
              >
                {formatCurrency(currency, teeTime.price, activeLanguage.code)}
              </strong>

              {isGolfhaftetRequest && !!teeTime.rackPrice && teeTime.rackPrice > teeTime.price && (
                <s>{formatCurrency(currency, teeTime.rackPrice, activeLanguage.code)}</s>
              )}
            </div>
          </div>
        )}

        <div className="d-flex justify-content-center" style={{ gap: 1 }}>
          {times(MAX_PLAYERS, index => (
            <span
              key={index}
              className={classNames(
                'border border-light rounded-circle',
                index + 1 <= teeTime.availablePlayers ? 'bg-success' : 'bg-danger'
              )}
              style={{ padding: 6 }}
            />
          ))}
        </div>
      </button>
      {isDisabled && (
        <UncontrolledTooltip target={teeTimeId}>
          {translate('default.teetime-players')}
        </UncontrolledTooltip>
      )}
      {tooltip && <Tooltip target={buttonRef} title={tooltip} popperClassName={styles.tooltip} />}
    </>
  );
};

export default TeeTime;
